import { useMemo } from 'react';
import type { DateTime, RideDetails } from '@/api';

export interface TimelineLocation {
	label: string;
	subText: string;
	time: DateTime;
}

export interface TimelineItemBase {
	id: string;
	iconType: 'location' | 'cab';
}

export interface LocationTimelineItem extends TimelineItemBase {
	type: 'pickup' | 'drop';
	iconType: 'location';
	location: TimelineLocation;
}

export interface CabTimelineItem extends TimelineItemBase {
	type: 'cab';
	iconType: 'cab';
	distance: string;
}

export type TimelineItem = LocationTimelineItem | CabTimelineItem;

export interface UseTimelineItemsResult {
	timelineItems: TimelineItem[];
	alwaysShowTimelineItem: TimelineItem;
	otherTimelineItems: TimelineItem[];
}

/**
 * Hook to generate timeline items from ride details
 *
 * This hook creates a standardized timeline structure for displaying
 * pickup, cab, and dropoff information in a consistent format.
 */
export function useTimelineItems(
	rideDetails: Omit<RideDetails, 'searchSessionId'>,
): UseTimelineItemsResult {
	return useMemo(() => {
		const timelineItems: TimelineItem[] = [
			{
				id: 'pickup',
				location: {
					label: rideDetails.pickup.location.formattedAddress,
					subText: 'Pickup At:',
					time: rideDetails.pickup.dateTime,
				},
				iconType: 'location' as const,
				type: 'pickup' as const,
			},
			{
				id: 'cab',
				iconType: 'cab' as const,
				distance: rideDetails.distance,
				type: 'cab' as const,
			},
			{
				id: 'drop',
				location: {
					label: rideDetails.drop.location.formattedAddress,
					subText: 'Estimated Drop-off:',
					time: rideDetails.drop.dateTime,
				},
				iconType: 'location' as const,
				type: 'drop' as const,
			},
		];

		const firstTimelineItem = timelineItems[0] as TimelineItem;
		const otherTimelineItems = timelineItems.slice(1);

		return {
			timelineItems,
			alwaysShowTimelineItem: firstTimelineItem,
			otherTimelineItems,
		};
	}, [rideDetails]);
}

export default useTimelineItems;
